<template>
    <div
        class="kleptos-file-system"
        :class="{ 'has-tree-view': treeView }"
    >
        <div>

            <!-- TOOLBAR -->
            <FileSystemToolbar
                :directoryBreadcrumbs="directoryBreadcrumbs"
                :iconView="iconView"
                @goHome="setDirectoryAndOpen(pathShortcuts.home)"
                @goUpDirectory="setDirectoryAndOpen($event)"
                @setIconView="iconView = !iconView"
            />

            <!-- ADDRESS BAR -->
            <FileSystemAddressBar
                :path="directoryLastInBreadcrumbs.path"
                :searchQuery="searchQuery"
            />

        </div>

        <!-- MULTIPANE -->
        <Multipane
            class="fs-multipane"
            layout="vertical">

            <!-- PANE -->
            <div
                class="fs-inset-pane kleptos-overflow"
                :style="{ minWidth: '220px', width: '240px', maxWidth: '70%' }"
            >
                <div class="fs-inset-pane-inner">

                    <!-- INFO VIEW -->
                    <div
                        v-show="!treeView"
                        class="fs-info-view"
                    >
                        <h2><i :class="'fa '+ node.icon"></i><br> {{ node.name }}</h2>
                        <hr>
                        <p>Your trusty hunk of silicon, defender of data. Choose your directory, if you dare.</p>
                    </div>

                    <!-- TREE VIEW -->
                    <TreeView
                        v-show="treeView"
                        :directory="localFileSystem"
                        ref="treeView"
                        @selected="setDirectory"
                    />

                </div>
            </div>

            <!-- MULTIPANE RESIZER -->
            <MultipaneResizer v-if="treeView"/>

            <!-- PANE -->
            <div class="fs-inset-pane flex-grow-1 kleptos-overflow">
                <div class="fs-inset-pane-inner">

                    <!-- LIST VIEW -->
                    <ListView
                        v-if="!iconView"
                        :directory="directoryContents"
                        :columns="directoryColumns"
                        :filter-key="searchQuery"
                    />
                    <!-- @select="directory = $event" this ain't used yet-->

                    <!-- ICON VIEW -->
                    <IconView
                        v-if="iconView"
                        :directory="directoryContents"
                        :constrain="true"
                    />

                </div>
            </div>
        </Multipane>

        <!-- STATUS BAR -->
        <div class="fs-status-bar mt-2">
            <span>{{ directoryContentsTotal }} items</span>
        </div>

    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import { Multipane, MultipaneResizer } from 'vue-multipane';
    import IconView from './FileSystemIconView/FileSystemIconView.vue';
    import ListView from './FileSystemListView/FileSystemListView.vue';
    import TreeView from './FileSystemTreeView/FileSystemTreeView.vue';
    import FileSystemToolbar from './FileSystemToolbar.vue';
    import FileSystemAddressBar from './FileSystemAddressBar.vue';

    // TODO:

    // * HISTORY: if folders - they open, add to history?
    // * LEAF: figure out how to calculate leaf (no folders left inside current to drill)
    // * CHROME: remove Google YELLOW from autofill thing on input field
    // * PATH: input will only update address on change()
    // * DOCKING: - dock file system - HIGH PRIORITY - docking prepends /Desktop/My Computer etc to the foreign system
    // * todo: setInitProp on store, send uid of window through to fileSystem - will save state on refresh (save paths)
    // * LIST_VIEW: listView doesn't have deselect working

    export default {
        name: 'FileSystem', // ?
        components: {
            IconView,
            ListView,
            Multipane,
            MultipaneResizer,
            TreeView,
            FileSystemToolbar,
            FileSystemAddressBar
        },
        props: {
            node: { // this.node
                type: Object,
                required: true
            }
        },
        data() {
            return {
                activeDirectory: {}, // active node (can set 'open' on it)
                directoryBreadcrumbs: [], // breadcrumbs
                directoryColumns: ['name', 'type', 'size', 'modified'],
                history: [], // add to history
                iconView: false, // false
                pathShortcuts: {
                    home: '/Desktop/My Computer/Drive/Home',
                    drive: '/Desktop/My Computer/Drive',
                },
                searchQuery: '',
                treeView: false
            }
        },
        computed: {
            ...mapGetters('gameOS', {
                localFileSystem: 'getLocalFileSystem', // HINT: PC_TEMPLATE: {...} - for TreeView only
                getDirectoriesFromPath: 'getDirectoriesFromPath'
            }),
            directoryContents() {
                return this.activeDirectory.children;
            },
            directoryContentsTotal() {
                return Number(this.directoryContents.length); // error can tell us if path has typo on the $store
            },
            directoryLastInBreadcrumbs() {
                return this.directoryBreadcrumbs[this.directoryBreadcrumbs.length-1].node;
            }
        },
        created() {
            // initProp coming in from the outside. Where do we want to go?
            const path = this.node.initProp ? this.node.initProp.path : this.pathShortcuts.drive;

            // set iconView from init prop
            if (this.node.initProp.iconView) this.iconView = this.node.initProp.iconView;
            if (this.node.initProp.treeView) this.treeView = this.node.initProp.treeView;

            // go there
            this.setDirectory(path);

            // open the folders (fix leaf though)
            this.openDirectories();
        },
        methods: {
            //-----------------------------------------------------------------
            // SET DIRECTORY
            //-----------------------------------------------------------------

            setDirectory(path) {

                // must get path string from 'created' or from selection (which passes direct object)
                path = typeof path === 'object' ? String(path.path) : path;

                // store breadcrumbs eg: [{ path: '/', node: {}}, ...] - how to get there
                this.directoryBreadcrumbs = this.getDirectoriesFromPath(path); // <-- this alters the fileSystem passed to the treeView

                // set the directory for this instance
                this.activeDirectory = this.directoryLastInBreadcrumbs; // <-- this laters the directory, second pane (no effect to treeview)

                // todo: setInitProp on store, send uid of window through to fileSystem
            },
            //-----------------------------------------------------------------
            // OPEN DIRECTORIES
            //-----------------------------------------------------------------

            openDirectories() {
                this.$nextTick(() => {
                    const allDirectories = this.$refs.treeView.$el.querySelectorAll('[data-path]');

                    // close all directories
                    allDirectories.forEach(el => { el.style.display = 'none'; });

                    //.slice(0,-1)
                    this.directoryBreadcrumbs.forEach(crumb => {
                        const activeDirectory = this.$refs.treeView.$el.querySelector('[data-path="'+ crumb.path +'"]');
                        activeDirectory.style.display = 'block';
                    })
                })
            },
            //-----------------------------------------------------------------
            // SET DIRECTORY AND OPEN
            // we don't want to collapse the directories when selecting
            // BUT we do want to when going home, up, back, forward etc
            //-----------------------------------------------------------------

            setDirectoryAndOpen(path) {
                this.setDirectory(path);
                this.openDirectories();
            },
            //-----------------------------------------------------------------
            // TEST
            //-----------------------------------------------------------------

            log(msg) {
                console.log(msg);
            }
        }
    }
</script>

<style lang="scss" scoped>

    //-----------------------------------------------------------------
    // FILE SYSTEM
    //-----------------------------------------------------------------

    .kleptos-file-system {
        display: flex;
        flex-direction: column;
        width: 100%;
        // border: 2px dashed white;
    }

    //-----------------------------------------------------------------
    // MULTIPANE
    //-----------------------------------------------------------------

    .fs-multipane {
        // flex-grow: 1;
        // width: 100%;
        height: 100%;
    }

    .fs-multipane .fs-inset-pane .fs-inset-pane-inner {
        position: absolute; // [LOCKED] - for scrollbars
        width: 100%; // [LOCKED]
        height: 100%; // [LOCKED]
    }

    .theme-95 .kleptos-file-system /deep/ .fs-inset-pane { // DEEP - could use a global?
        border: 1px solid rgba(black, 0.25); // [LOCKED]
        border-width: 2px 0 0 2px;
        background: lighten(#ebece3, 5%); // this is good
    }

    // remove left border for no tree-view
    .theme-95 .kleptos-file-system:not(.has-tree-view) /deep/ .fs-inset-pane { // DEEP - could use a global?
        border-left: 0;
    }

    .theme-os .kleptos-file-system /deep/ .fs-inset-pane {
        border: 1px solid rgba(black, 0.6);
        border-width: 2px 0 0 2px;
        background: lighten(#1e1e1e, 8%);
    }

    //-----------------------------------------------------------------
    // MULTIPANE ICONS (TREEVIEW ETC)
    //-----------------------------------------------------------------

    .fs-multipane /deep/ .fa {
        color: #808080; // yellowy
        margin-right: 5px;
    }

    .fs-multipane /deep/ .fa-folder,
    .fs-multipane /deep/ .fa-folder-open {
        color: #f5d67c;
        text-shadow: -0.5px 0.5px 1px rgba(black, 0.4);
    }

    //-----------------------------------------------------------------
    // MULTIPANE RESIZER
    //-----------------------------------------------------------------

    // no tree view
    .fs-multipane > .multipane-resizer {
        // position: relative; // hint
        // top: 20px; // hint
        left: 0; // lib correction
        width: rem-calc(10);
        min-width: rem-calc(10);
        margin-left: 0; // lib correction
    }

    // has tree-view
    .has-tree-view .fs-multipane > .multipane-resizer {
        background: inherit;

        &:before {
            display: block;
            content: "";
            width: 3px;
            height: 30px;
            position: absolute;
            top: 50%;
            left: calc(50% - 1px);
            margin-top: -15px;
            background: darken(#c7c6b7, 10%);
            opacity: 0;
        }

        &:hover:before {
            opacity: 1;
        }
    }

    //-----------------------------------------------------------------
    // INFO VIEW
    // shown when there's no tree view
    //-----------------------------------------------------------------

    .fs-info-view {
        height: 100%;
        padding: rem-calc(15 35 15 20);
        font-size: rem-calc(14);
    }

    .fs-info-view hr {
        height: 2px;
        border: 0;
        background: linear-gradient(to right, #b7bc56 0%,#4c7072 44%,#7baf36 100%);
    }

    //-----------------------------------------------------------------
    // STATUS BAR (OPTIONAL)
    //-----------------------------------------------------------------

    .fs-status-bar {
        font-size: rem-calc(15);
    }
</style>